import { Suspense } from 'react';
import { lazyWithReload } from './lib/lazyWithReload';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PageLoader } from './components/ui/Spinner';
import { LoginPage } from './pages/LoginPage';
import { LandingPage } from './pages/LandingPage';
import { PrivacyPolicyPage, TermsPage, DeleteAccountPage } from './pages/LegalPages';
import { useAuth } from './lib/auth';
import { CookieConsent, GaPageTracker } from './components/CookieConsent';
import { ScrollToTop } from './components/ScrollToTop';

/**
 * v0.11.23: Trasa `/` rozróżnia zalogowanych i anonimowych użytkowników.
 *
 * Wcześniej PWA na iOS/Android startowało zawsze na LandingPage — user
 * z aktywną sesją musiał klikać „Przejdź do logowania" i był loginowany
 * automatycznie (sesja żyła). UX problem: 2 dodatkowe kliki przy każdym
 * uruchomieniu PWA.
 *
 * Teraz: zalogowany → redirect do `/dashboard`. Anonimowy → LandingPage.
 * Loading state (auth provider sprawdza session w `restoreSession`) →
 * spinner, żeby uniknąć flash landing-then-redirect.
 */
function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}
const InvitePage = lazyWithReload(() => import('./pages/InvitePage').then(m => ({ default: m.InvitePage })));
const ForgotPasswordPage = lazyWithReload(() => import('./pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazyWithReload(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const RequestAccessPage = lazyWithReload(() => import('./pages/RequestAccessPage').then(m => ({ default: m.RequestAccessPage })));
const AccessRequestVerifyPage = lazyWithReload(() => import('./pages/AccessRequestVerifyPage').then(m => ({ default: m.AccessRequestVerifyPage })));
const AccessRequestsPage = lazyWithReload(() => import('./pages/admin/AccessRequestsPage').then(m => ({ default: m.AccessRequestsPage })));
const FeaturesPage = lazyWithReload(() => import('./pages/marketing/FeaturesPage').then(m => ({ default: m.FeaturesPage })));
const UseCasesHubPage = lazyWithReload(() => import('./pages/marketing/UseCasesHubPage').then(m => ({ default: m.UseCasesHubPage })));
const UseCasePage = lazyWithReload(() => import('./pages/marketing/UseCasePage').then(m => ({ default: m.UseCasePage })));
const PricingPage = lazyWithReload(() => import('./pages/marketing/PricingPage').then(m => ({ default: m.PricingPage })));
const BlogIndexPage = lazyWithReload(() => import('./pages/marketing/BlogIndexPage').then(m => ({ default: m.BlogIndexPage })));
const BlogPostPage = lazyWithReload(() => import('./pages/marketing/BlogPostPage').then(m => ({ default: m.BlogPostPage })));

// Lazy-loaded pages (code-splitting)
const DashboardPage = lazyWithReload(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const DashboardOverviewPage = lazyWithReload(() => import('./pages/DashboardOverviewPage').then(m => ({ default: m.DashboardOverviewPage })));
const HelpPage = lazyWithReload(() => import('./pages/HelpPage').then(m => ({ default: m.HelpPage })));
const ProjectDetailLayout = lazyWithReload(() => import('./pages/projects/ProjectDetailLayout').then(m => ({ default: m.ProjectDetailLayout })));
const ProjectObjectsTab = lazyWithReload(() => import('./pages/projects/ProjectObjectsTab').then(m => ({ default: m.ProjectObjectsTab })));
const ProjectDocumentsTab = lazyWithReload(() => import('./pages/projects/ProjectDocumentsTab').then(m => ({ default: m.ProjectDocumentsTab })));
const ProjectFormsTab = lazyWithReload(() => import('./pages/projects/ProjectFormsTab').then(m => ({ default: m.ProjectFormsTab })));
const ProjectGalleryTab = lazyWithReload(() => import('./pages/projects/ProjectGalleryTab').then(m => ({ default: m.ProjectGalleryTab })));
// Sprint PM-4 (v0.60.0) — web project-level photo map. Lazy (Leaflet w bundlu).
const ProjectPhotoMapTab = lazyWithReload(() => import('./pages/projects/ProjectPhotoMapTab').then(m => ({ default: m.ProjectPhotoMapTab })));
const ProjectInfoPage = lazyWithReload(() => import('./pages/projects/ProjectInfoPage').then(m => ({ default: m.ProjectInfoPage })));
const ProjectDiaryPage = lazyWithReload(() => import('./pages/projects/ProjectDiaryPage').then(m => ({ default: m.ProjectDiaryPage })));
const ObjectDetailPage = lazyWithReload(() => import('./pages/projects/ObjectDetailPage').then(m => ({ default: m.ObjectDetailPage })));
const OrganizationsPage = lazyWithReload(() => import('./pages/admin/OrganizationsPage').then(m => ({ default: m.OrganizationsPage })));
const UsersPage = lazyWithReload(() => import('./pages/admin/UsersPage').then(m => ({ default: m.UsersPage })));
const ProjectStatsPage = lazyWithReload(() => import('./pages/stats/ProjectStatsPage').then(m => ({ default: m.ProjectStatsPage })));
const ExportsPage = lazyWithReload(() => import('./pages/projects/ExportsPage').then(m => ({ default: m.ExportsPage })));
const SyncDashboardPage = lazyWithReload(() => import('./pages/sync/SyncDashboardPage').then(m => ({ default: m.SyncDashboardPage })));
const ProjectSettingsPage = lazyWithReload(() => import('./pages/projects/ProjectSettingsPage').then(m => ({ default: m.ProjectSettingsPage })));
const DictionariesPage = lazyWithReload(() => import('./pages/admin/DictionariesPage').then(m => ({ default: m.DictionariesPage })));
const SymbolLibraryPage = lazyWithReload(() => import('./pages/admin/SymbolLibraryPage').then(m => ({ default: m.SymbolLibraryPage })));
// Leniwe ładowanie jest tu wymogiem, nie optymalizacją: edytor symboli to
// największy pojedynczy kawałek UI w aplikacji, a większość użytkowników
// nigdy go nie otworzy.
const SymbolBuilderPage = lazyWithReload(() =>
  import('./pages/admin/symbol-builder/SymbolBuilderPage').then((m) => ({
    default: m.SymbolBuilderPage,
  })),
);
const OrganizationProfilePage = lazyWithReload(() => import('./pages/admin/OrganizationProfilePage').then(m => ({ default: m.OrganizationProfilePage })));
const OrganizationStoragePage = lazyWithReload(() => import('./pages/admin/OrganizationStoragePage').then(m => ({ default: m.OrganizationStoragePage })));
const ProjectStoragePage = lazyWithReload(() => import('./pages/ProjectStoragePage').then(m => ({ default: m.ProjectStoragePage })));
const ObjectStoragePage = lazyWithReload(() => import('./pages/ObjectStoragePage').then(m => ({ default: m.ObjectStoragePage })));
const StorageQuotasPage = lazyWithReload(() => import('./pages/admin/StorageQuotasPage').then(m => ({ default: m.StorageQuotasPage })));
const AiUsagePage = lazyWithReload(() => import('./pages/admin/AiUsagePage').then(m => ({ default: m.AiUsagePage })));
const StorageTrashPage = lazyWithReload(() => import('./pages/admin/StorageTrashPage').then(m => ({ default: m.StorageTrashPage })));
const ReportTemplatesPage = lazyWithReload(() => import('./pages/admin/ReportTemplatesPage').then(m => ({ default: m.ReportTemplatesPage })));
const TitlePagesPage = lazyWithReload(() => import('./pages/admin/TitlePagesPage').then(m => ({ default: m.TitlePagesPage })));
const TitlePageEditorPage = lazyWithReload(() => import('./pages/admin/TitlePageEditorPage').then(m => ({ default: m.TitlePageEditorPage })));
const CustomVariablesPage = lazyWithReload(() => import('./pages/admin/CustomVariablesPage').then(m => ({ default: m.CustomVariablesPage })));
const ClientsPage = lazyWithReload(() => import('./pages/admin/ClientsPage').then(m => ({ default: m.ClientsPage })));
const ClientDetailPage = lazyWithReload(() => import('./pages/admin/ClientDetailPage').then(m => ({ default: m.ClientDetailPage })));
const FormsPage = lazyWithReload(() => import('./pages/admin/FormsPage').then(m => ({ default: m.FormsPage })));
const FormEditPage = lazyWithReload(() => import('./pages/admin/FormEditPage').then(m => ({ default: m.FormEditPage })));
const FormSubmissionPage = lazyWithReload(() => import('./pages/forms/FormSubmissionPage').then(m => ({ default: m.FormSubmissionPage })));
const FormSubmissionPreviewPage = lazyWithReload(() => import('./pages/forms/FormSubmissionPreviewPage').then(m => ({ default: m.FormSubmissionPreviewPage })));
const SubmissionsListPage = lazyWithReload(() => import('./pages/forms/SubmissionsListPage').then(m => ({ default: m.SubmissionsListPage })));
const TasksPage = lazyWithReload(() => import('./pages/projects/TasksPage').then(m => ({ default: m.TasksPage })));
const NotificationSettingsPage = lazyWithReload(() => import('./pages/settings/NotificationSettingsPage').then(m => ({ default: m.NotificationSettingsPage })));
const ProfilePage = lazyWithReload(() => import('./pages/settings/ProfilePage').then(m => ({ default: m.ProfilePage })));
const EmailSettingsPage = lazyWithReload(() => import('./pages/admin/EmailSettingsPage').then(m => ({ default: m.EmailSettingsPage })));
const PhotoOverlayPresetsPage = lazyWithReload(() => import('./pages/admin/PhotoOverlayPresetsPage').then(m => ({ default: m.PhotoOverlayPresetsPage })));
const BackupsPage = lazyWithReload(() => import('./pages/admin/BackupsPage').then(m => ({ default: m.BackupsPage })));
const PendingApprovalsPage = lazyWithReload(() => import('./pages/forms/PendingApprovalsPage').then(m => ({ default: m.PendingApprovalsPage })));
const AuditLogPage = lazyWithReload(() => import('./pages/admin/AuditLogPage').then(m => ({ default: m.AuditLogPage })));

function SuspenseLoader({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

export function App() {
  return (
    <>
    <ScrollToTop />
    <CookieConsent />
    <GaPageTracker />
    <Routes>
      <Route path="/" element={<HomeRoute />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/invite/:token" element={<SuspenseLoader><InvitePage /></SuspenseLoader>} />
      <Route path="/forgot-password" element={<SuspenseLoader><ForgotPasswordPage /></SuspenseLoader>} />
      <Route path="/reset-password/:token" element={<SuspenseLoader><ResetPasswordPage /></SuspenseLoader>} />
      <Route path="/request-access" element={<SuspenseLoader><RequestAccessPage /></SuspenseLoader>} />
      <Route path="/access-request/verify/:token" element={<SuspenseLoader><AccessRequestVerifyPage /></SuspenseLoader>} />
      <Route path="/funkcje" element={<SuspenseLoader><FeaturesPage /></SuspenseLoader>} />
      <Route path="/zastosowania" element={<SuspenseLoader><UseCasesHubPage /></SuspenseLoader>} />
      <Route path="/zastosowania/:slug" element={<SuspenseLoader><UseCasePage /></SuspenseLoader>} />
      <Route path="/cennik" element={<SuspenseLoader><PricingPage /></SuspenseLoader>} />
      <Route path="/blog" element={<SuspenseLoader><BlogIndexPage /></SuspenseLoader>} />
      <Route path="/blog/:slug" element={<SuspenseLoader><BlogPostPage /></SuspenseLoader>} />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <SuspenseLoader><DashboardPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/overview"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']}>
            <SuspenseLoader><DashboardOverviewPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <SuspenseLoader><HelpPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      {/* Project detail - tabbed layout with nested routes */}
      <Route
        path="/projects/:id"
        element={
          <ProtectedRoute>
            <SuspenseLoader><ProjectDetailLayout /></SuspenseLoader>
          </ProtectedRoute>
        }
      >
        <Route index element={<SuspenseLoader><ProjectObjectsTab /></SuspenseLoader>} />
        <Route path="info" element={<SuspenseLoader><ProjectInfoPage /></SuspenseLoader>} />
        <Route path="diary" element={<SuspenseLoader><ProjectDiaryPage /></SuspenseLoader>} />
        <Route path="tasks" element={<SuspenseLoader><TasksPage /></SuspenseLoader>} />
        <Route path="documents" element={<SuspenseLoader><ProjectDocumentsTab /></SuspenseLoader>} />
        <Route path="forms" element={<SuspenseLoader><ProjectFormsTab /></SuspenseLoader>} />
        <Route path="gallery" element={<SuspenseLoader><ProjectGalleryTab /></SuspenseLoader>} />
        <Route path="photo-map" element={<SuspenseLoader><ProjectPhotoMapTab /></SuspenseLoader>} />
        <Route path="stats" element={<SuspenseLoader><ProjectStatsPage /></SuspenseLoader>} />
        <Route path="exports" element={<SuspenseLoader><ExportsPage /></SuspenseLoader>} />
        <Route
          path="settings"
          element={
            <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']}>
              <SuspenseLoader><ProjectSettingsPage /></SuspenseLoader>
            </ProtectedRoute>
          }
        />
      </Route>

      <Route
        path="/objects/:id"
        element={
          <ProtectedRoute>
            <SuspenseLoader><ObjectDetailPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/sync"
        element={
          <ProtectedRoute>
            <SuspenseLoader><SyncDashboardPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/organizations"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN']}>
            <SuspenseLoader><OrganizationsPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/users"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']}>
            <SuspenseLoader><UsersPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/dictionaries"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']} permission="dictionaries">
            <SuspenseLoader><DictionariesPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/symbols"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']} permission="symbols">
            <SuspenseLoader><SymbolLibraryPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/symbols/builder"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']} permission="symbols">
            <SuspenseLoader><SymbolBuilderPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/organization"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']}>
            <SuspenseLoader><OrganizationProfilePage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/organization/storage"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']}>
            <SuspenseLoader><OrganizationStoragePage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/projects/:id/storage"
        element={
          <ProtectedRoute>
            <SuspenseLoader><ProjectStoragePage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/objects/:id/storage"
        element={
          <ProtectedRoute>
            <SuspenseLoader><ObjectStoragePage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/super-admin/storage/quotas"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN']}>
            <SuspenseLoader><StorageQuotasPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/super-admin/ai/usage"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN']}>
            <SuspenseLoader><AiUsagePage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/organization/storage/trash"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']}>
            <SuspenseLoader><StorageTrashPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/report-templates"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']} permission="report-templates">
            <SuspenseLoader><ReportTemplatesPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/title-pages"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']} permission="title-pages">
            <SuspenseLoader><TitlePagesPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/title-pages/edit/:id"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']} permission="title-pages">
            <SuspenseLoader><TitlePageEditorPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/custom-variables"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']} permission="custom-variables">
            <SuspenseLoader><CustomVariablesPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/clients"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']} permission="clients">
            <SuspenseLoader><ClientsPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/clients/:id"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']} permission="clients">
            <SuspenseLoader><ClientDetailPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/forms"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']} permission="forms">
            <SuspenseLoader><FormsPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/forms/:id/edit"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']} permission="forms">
            <SuspenseLoader><FormEditPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/form-submissions/:id"
        element={
          <ProtectedRoute>
            <SuspenseLoader><FormSubmissionPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/form-submissions/:id/preview"
        element={
          <ProtectedRoute>
            <SuspenseLoader><FormSubmissionPreviewPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/form-assignments/:assignmentId/submissions"
        element={
          <ProtectedRoute>
            <SuspenseLoader><SubmissionsListPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/notifications"
        element={
          <ProtectedRoute>
            <SuspenseLoader><NotificationSettingsPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/profile"
        element={
          <ProtectedRoute>
            <SuspenseLoader><ProfilePage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/organization/email-settings"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']}>
            <SuspenseLoader><EmailSettingsPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/photo-overlay-presets"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']}>
            <SuspenseLoader><PhotoOverlayPresetsPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/backups"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN']}>
            <SuspenseLoader><BackupsPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/approvals"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']}>
            <SuspenseLoader><PendingApprovalsPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/audit-log"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN']}>
            <SuspenseLoader><AuditLogPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/access-requests"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN']}>
            <SuspenseLoader><AccessRequestsPage /></SuspenseLoader>
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
    </>
  );
}
